import { Suspense, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { Header } from '@/app/layout/Header';
import { achatarNav, ITENS_NAV, Sidebar } from '@/app/layout/Sidebar';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useHeartbeatDeAtividade } from '@/features/auth/hooks/useHeartbeatDeAtividade';
import { BotaoFlutuanteSuporte } from '@/features/suporte/components/BotaoFlutuanteSuporte';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * Shell da aplicação. Dimensões e comportamento portados da V1
 * (`Hormonalle/src/pages/Layout.tsx`): sidebar fixa de 64 (w-64) que não colapsa
 * no desktop, off-canvas no mobile, conteúdo com `lg:pl-64`.
 */
export function AppLayout() {
  const [menuAberto, setMenuAberto] = useState(false);
  const { me, sair } = useAuth();
  const { pathname } = useLocation();

  // Este layout só renderiza dentro de `PrivateRoute` — `me` presente aqui é
  // exatamente "há sessão", que é a condição para o heartbeat existir.
  useHeartbeatDeAtividade(Boolean(me));

  // Achata a árvore antes de procurar: sem isso, os itens dentro do grupo
  // "Administração" não são encontrados e TODA tela admin vira "Sistema".
  //
  // Ordena do caminho mais longo para o mais curto para `/admin/cargos` não ser
  // capturado por um eventual `/admin` — `startsWith` casa com o primeiro.
  const titulo =
    achatarNav(ITENS_NAV)
      .filter((item) => pathname.startsWith(item.para))
      .sort((a, b) => b.para.length - a.para.length)[0]?.nome ?? 'Sistema';

  return (
    <div className="min-h-screen bg-surface">
      {/* `print:hidden`: sem isto, o Ctrl+P de qualquer tela sai com a
          navegação inteira em volta do conteúdo — inclusive o menu off-canvas
          do mobile, que é posicionado fixo e imprimiria por cima de tudo. */}
      <div className="print:hidden">
        <Sidebar
          aberta={menuAberto}
          aoFechar={() => setMenuAberto(false)}
          aoSair={sair}
          permissoes={me?.permissoes}
        />
      </div>

      <div className="lg:pl-64 print:pl-0">
        <div className="print:hidden">
          <Header
            titulo={titulo}
            nomeCompleto={me?.perfil.nomeCompleto}
            email={me?.usuario.email}
            crmv={me?.perfil.crmv}
            ufCrmv={me?.perfil.ufCrmv}
            fotoUrl={me?.perfil.fotoUrl}
            aoAbrirMenu={() => setMenuAberto(true)}
          />
        </div>

        <main className="p-6 lg:p-8 print:p-0">
          {/* Um throw numa página não pode derrubar o shell inteiro. */}
          <ErrorBoundary>
            {/*
              O `Suspense` fica AQUI, em volta do `Outlet`, e não em volta de
              cada rota: assim a sidebar e o header não piscam enquanto o chunk
              de uma rota `React.lazy` baixa, e toda rota adiada que surgir
              depois já nasce coberta. Hoje só a Análise Comparativa é lazy —
              ela carrega o `recharts`, que ninguém mais usa.
            */}
            <Suspense fallback={<Skeleton className="h-64 w-full rounded-lg" />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      {/*
        IRMÃO do container com `lg:pl-64`, não filho dele: o botão é `fixed`, e
        o padding da sidebar não o afetaria de qualquer forma — mas dentro do
        `<main>` ele herdaria o `p-6 lg:p-8` no cálculo de quem o contém e
        ficaria preso ao scroll do conteúdo em alguns navegadores.

        Ele mesmo decide se aparece (permissão + rota atual) — ver
        `BotaoFlutuanteSuporte`.
      */}
      <BotaoFlutuanteSuporte />
    </div>
  );
}
